import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { readRememberedAdminUsername, persistRememberedAdminUsername } from "@/lib/adminRememberedUsername";

const TRIGGER_STYLES = {
  // Sits in the light navbar next to the phone number/CTA: a subtle bordered "ghost"
  // pill rather than a plain link, so it never competes with the main "Talep Oluştur"
  // CTA button.
  navbar:
    "text-[color:hsl(var(--muted-foreground))] border-[color:var(--line)] bg-transparent hover:text-[color:var(--brand-charcoal)] hover:border-[color:var(--brand-gold)] hover:shadow-[0_0_14px_rgba(212,175,55,0.25)]",
  // Sits at the very bottom of the mobile hamburger menu, below the public nav
  // entries - same subtle "ghost" pill treatment, just stretched full-width by the
  // caller for a comfortable touch target.
  mobile:
    "text-[color:hsl(var(--muted-foreground))] border-[color:var(--line)] bg-transparent hover:text-[color:var(--brand-charcoal)] hover:border-[color:var(--brand-gold)] hover:shadow-[0_0_14px_rgba(212,175,55,0.25)]",
};

const TRIGGER_LABELS = {
  navbar: "Yönetici Girişi",
  mobile: "Yönetici Girişi",
};

/**
 * "Ghost button" trigger + login modal used to let the office owner reach the
 * Admin Panel directly from the public site (no need to type /yonetim/giris).
 * Kept intentionally low-contrast/transparent everywhere it appears (desktop navbar +
 * bottom of the mobile hamburger menu only) so it never competes with the
 * client-facing "Quiet Luxury" content, and there is exactly ONE visible admin entry
 * point per page (one by breakpoint: navbar on desktop, hamburger menu on mobile).
 *
 */
export const AdminLoginButton = ({ variant = "navbar", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState(() => readRememberedAdminUsername());
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAdminAuth();
  const navigate = useNavigate();

  const resetAndClose = () => {
    setOpen(false);
    // Hatırlanan bir kullanıcı adı varsa formu kapatırken onu kaybetmeyelim - sadece
    // şifre ve hata mesajı sıfırlanır. Bir dahaki açılışta tekrar okunacağı için burada
    // username'i boşaltmak gerekmez, ama tutarlılık için en güncel kalıcı değeri
    // yeniden okuyoruz.
    setUsername(readRememberedAdminUsername());
    setPassword("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(username, password, rememberMe);
    setLoading(false);
    if (result.success) {
      // Sadece kullanıcı adı kalıcı depolanır - şifre asla.
      persistRememberedAdminUsername(rememberMe, username);
      resetAndClose();
      navigate("/yonetim");
    } else {
      setError(result.message);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`${variant}-admin-login-button`}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 text-xs font-medium tracking-wide rounded-full px-4 py-2 border transition-[color,border-color,box-shadow,background-color] duration-200 ${TRIGGER_STYLES[variant]} ${className}`}
      >
        <ShieldCheck className="h-3.5 w-3.5" /> {TRIGGER_LABELS[variant]}
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : resetAndClose())}>
        <DialogContent className="sm:max-w-sm bg-[color:hsl(var(--card))]" data-testid="admin-login-dialog">
          <DialogHeader className="flex flex-col items-center gap-3 text-center">
            <BrandLogo asLink={false} stacked iconClassName="h-12 w-auto" nameClassName="text-lg" taglineClassName="text-[9px]" />
            <DialogTitle className="font-[var(--font-serif)] text-lg">Yönetici Girişi</DialogTitle>
            <DialogDescription>Devam etmek için giriş bilgilerinizi girin.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="admin-modal-username">Kullanıcı Adı</Label>
              <Input
                id="admin-modal-username"
                data-testid="admin-modal-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="admin-modal-password">Şifre</Label>
              <Input
                id="admin-modal-password"
                type="password"
                data-testid="admin-modal-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="admin-modal-remember-me"
                data-testid="admin-modal-remember-me-checkbox"
                checked={rememberMe}
                onCheckedChange={(v) => setRememberMe(Boolean(v))}
              />
              <Label htmlFor="admin-modal-remember-me" className="text-sm font-normal text-[color:hsl(var(--muted-foreground))] cursor-pointer">
                Beni Hatırla
              </Label>
            </div>
            {error && (
              <p className="text-sm text-[color:hsl(var(--destructive))]" data-testid="admin-modal-login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="admin-modal-login-submit-button"
              disabled={loading}
              className="h-11 rounded-[10px] bg-[linear-gradient(135deg,#D4AF37_0%,#C5A059_55%,#E7D7A6_100%)] text-[color:var(--brand-charcoal)] font-medium mt-1"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Giriş Yap
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminLoginButton;

